import { useCallback, useState, type ReactNode } from 'react';
import {
  CircleNotchIcon,
  PencilSimpleIcon,
  SealCheckIcon,
  TrashIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { msg, T, useGT, useMessages, Var } from 'gt-react';
import type { AgentDefinitionStaleBaseWarning } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { useToast } from 'src/context/ToastContext';
import { AgentActionTooltip } from './AgentActionTooltip';
import { useAgentDefinitionForm } from './AgentDefinitionFormContext';
import {
  editButtonMode,
  editTooltipKey,
  errorMessage,
  reactToFinalize,
  type DraftNotice,
  type EditTooltipKey,
} from './agentDefinitionEditActions';

const EDIT_TOOLTIP = {
  create: msg(
    'Copy the version you are viewing into a draft. Published runs stay on the current version until you publish.',
    { context: 'tooltip for the edit agent button when no draft exists' },
  ),
  conflict: msg(
    'You already have a draft. You will choose whether to resume it or delete it and copy the version you are viewing.',
    {
      context: 'tooltip for the edit agent button when a draft already exists',
    },
  ),
  not_finalized: msg(
    'Start a draft from a finalized version. This version is a draft you cannot edit.',
    {
      context: 'tooltip when edit is unavailable on someone else\u2019s draft',
    },
  ),
  loading: msg('Checking whether you already have a draft.', {
    context: 'tooltip while agent version history is loading',
  }),
  unavailable: msg('This agent has no version to edit yet.', {
    context: 'tooltip when an agent cannot be edited',
  }),
  hidden: msg('Edit', { context: 'unused edit tooltip' }),
} satisfies Record<EditTooltipKey, ReturnType<typeof msg>>;

const SAVE_DRAFT_TOOLTIP = msg(
  'Save your edits to this draft only. Published runs keep using the current published version.',
  { context: 'tooltip for the save agent draft button' },
);

const FINALIZE_TOOLTIP = msg(
  'Lock this draft so it cannot be edited. The next step asks whether new runs should use it or stay on the current published version. Unsaved edits are saved first.',
  { context: 'tooltip for the finalize agent draft button' },
);

const DISCARD_TOOLTIP = msg(
  'Delete this unpublished draft and go back to the published version. This does not archive the agent.',
  { context: 'tooltip for the discard agent draft button' },
);

export function AgentDraftEditorActions() {
  const { isEditable } = useAgentDefinitionForm();
  if (isEditable) return <OwnDraftActions />;
  return <EditAgentButton />;
}

export function EditAgentButton({ className }: { className?: string }) {
  const m = useMessages();
  const { editPlan, beginEdit, isDraftActionPending } =
    useAgentDefinitionForm();
  const mode = editButtonMode(editPlan.kind);
  if (mode === 'hidden') return null;
  return (
    <AgentActionTooltip label={m(EDIT_TOOLTIP[editTooltipKey(editPlan.kind)])}>
      <span className="inline-flex">
        <Button
          onClick={() => void beginEdit()}
          disabled={editButtonDisabled(mode, isDraftActionPending)}
          size="sm"
          className={className}
        >
          <EditButtonIcon
            pending={isDraftActionPending && mode === 'enabled'}
          />
          <T context="button, create or resume an agent definition draft">
            Edit
          </T>
        </Button>
      </span>
    </AgentActionTooltip>
  );
}

export function SaveDraftButton({
  label,
  className,
}: {
  label?: ReactNode;
  className?: string;
}) {
  const m = useMessages();
  const { isDirty, save, isSaving } = useAgentDefinitionForm();
  return (
    <AgentActionTooltip label={m(SAVE_DRAFT_TOOLTIP)}>
      <span className="inline-flex">
        <Button
          onClick={() => void save()}
          disabled={saveDisabled(isDirty, isSaving)}
          variant="outline"
          size="sm"
          className={className}
        >
          <SavingIcon saving={isSaving} />
          {label ?? (
            <T context="button, save agent definition draft">Save draft</T>
          )}
        </Button>
      </span>
    </AgentActionTooltip>
  );
}

export function FinalizeDraftButton() {
  const gt = useGT();
  const m = useMessages();
  const { showToast } = useToast();
  const { finalizeDraft, isSaving, isDraftActionPending } =
    useAgentDefinitionForm();
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [publishOnFinalize, setPublishOnFinalize] = useState(true);
  const [staleWarning, setStaleWarning] =
    useState<AgentDefinitionStaleBaseWarning | null>(null);
  const pending = isSaving || isDraftActionPending;

  async function handleFinalize(acknowledgeStaleBase = false) {
    try {
      const outcome = await finalizeDraft(
        publishOnFinalize,
        acknowledgeStaleBase,
      );
      reactToFinalize(outcome, {
        showStale: (warning) => {
          setFinalizeOpen(false);
          setStaleWarning(warning);
        },
        closeAndToast: () => {
          setFinalizeOpen(false);
          setStaleWarning(null);
          const toast = finalizeResultToast(gt, publishOnFinalize);
          showToast({ ...toast, color: 'success' });
        },
      });
    } catch (finalizeError) {
      showToast({
        title: gt('Could not finalize draft', {
          context: 'toast title after agent draft finalization fails',
        }),
        message: errorMessage(finalizeError),
        color: 'error',
      });
    }
  }

  return (
    <>
      <AgentActionTooltip label={m(FINALIZE_TOOLTIP)}>
        <span className="inline-flex">
          <Button
            onClick={() => {
              setPublishOnFinalize(true);
              setFinalizeOpen(true);
            }}
            disabled={pending}
            size="sm"
          >
            <FinalizeIcon pending={isDraftActionPending} />
            <T context="button, finalize agent definition draft">
              Finalize draft
            </T>
          </Button>
        </span>
      </AgentActionTooltip>
      <FinalizeDraftDialog
        open={finalizeOpen}
        publish={publishOnFinalize}
        pending={pending}
        onPublishChange={setPublishOnFinalize}
        onClose={() => setFinalizeOpen(false)}
        onConfirm={() => void handleFinalize()}
      />
      <StaleFinalizeDialog
        warning={staleWarning}
        pending={isDraftActionPending}
        onClose={() => setStaleWarning(null)}
        onConfirm={() => void handleFinalize(true)}
      />
    </>
  );
}

export function DiscardAgentDraftButton() {
  const m = useMessages();
  const { currentVersion, isEditable, isDraftActionPending } =
    useAgentDefinitionForm();
  const [open, setOpen] = useState(false);
  const discard = useConfirmDiscardDraft();
  if (!canDiscard(isEditable, currentVersion)) return null;
  return (
    <>
      <AgentActionTooltip label={m(DISCARD_TOOLTIP)}>
        <span className="inline-flex">
          <Button
            onClick={() => setOpen(true)}
            disabled={isDraftActionPending}
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            <T context="button, delete the agent draft being edited">
              Discard draft
            </T>
          </Button>
        </span>
      </AgentActionTooltip>
      <DiscardDraftConfirmDialog
        open={open}
        version={currentVersion.version}
        pending={isDraftActionPending}
        onClose={() => setOpen(false)}
        onConfirm={() => {
          void discard(currentVersion.id).then((deleted) => {
            if (deleted) setOpen(false);
          });
        }}
      />
    </>
  );
}

export function AgentDefinitionDraftBanner() {
  const { draftNotice } = useAgentDefinitionForm();
  if (draftNotice.kind === 'editing') {
    return <EditingDraftBanner notice={draftNotice} />;
  }
  if (draftNotice.kind === 'elsewhere') {
    return <ElsewhereDraftBanner notice={draftNotice} />;
  }
  return null;
}

function OwnDraftActions() {
  return (
    <>
      <SaveDraftButton />
      <FinalizeDraftButton />
      <DiscardAgentDraftButton />
    </>
  );
}

function EditButtonIcon({ pending }: { pending: boolean }) {
  if (pending) {
    return <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />;
  }
  return <PencilSimpleIcon className="mr-2 h-4 w-4" />;
}

function SavingIcon({ saving }: { saving: boolean }) {
  if (!saving) return null;
  return <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />;
}

function FinalizeIcon({ pending }: { pending: boolean }) {
  if (pending) {
    return <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />;
  }
  return <SealCheckIcon className="mr-2 h-4 w-4" />;
}

function editButtonDisabled(
  mode: 'enabled' | 'disabled',
  pending: boolean,
): boolean {
  if (mode === 'disabled') return true;
  return pending;
}

function saveDisabled(isDirty: boolean, isSaving: boolean): boolean {
  if (!isDirty) return true;
  return isSaving;
}

function canDiscard(
  isEditable: boolean,
  version: { id: string; version: number } | null,
): version is { id: string; version: number } {
  return isEditable && version !== null;
}

function finalizeResultToast(
  gt: ReturnType<typeof useGT>,
  publish: boolean,
): { title: string; message: string } {
  if (publish) {
    return {
      title: gt('Draft published', {
        context: 'toast after finalizing and publishing an agent draft',
      }),
      message: gt('New agent runs will use this version.', {
        context: 'toast after publishing an agent version',
      }),
    };
  }
  return {
    title: gt('Draft finalized', {
      context: 'toast after finalizing an agent draft',
    }),
    message: gt('The version is finalized and can be published later.', {
      context: 'toast after finalizing an agent version',
    }),
  };
}

function FinalizeDraftDialog({
  open,
  publish,
  pending,
  onPublishChange,
  onClose,
  onConfirm,
}: {
  open: boolean;
  publish: boolean;
  pending: boolean;
  onPublishChange: (publish: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="dialog title, finalize an agent definition draft">
              Finalize this draft?
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <T context="dialog description, finalize an agent definition draft">
              Finalizing locks this draft. It can no longer be edited.
              Publishing makes new runs use it. Leaving it unpublished keeps the
              current published version in place.
            </T>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-start gap-2 rounded-md border p-3">
          <Checkbox
            id="publish-agent-version"
            checked={publish}
            onCheckedChange={(checked) => onPublishChange(checked === true)}
          />
          <Label htmlFor="publish-agent-version" className="leading-5">
            <T context="checkbox label when finalizing an agent draft">
              Publish immediately for new agent runs
            </T>
          </Label>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            <T context="button, cancel agent draft finalization">Cancel</T>
          </AlertDialogCancel>
          <Button disabled={pending} onClick={onConfirm}>
            <PendingIcon pending={pending} />
            <FinalizeConfirmLabel publish={publish} />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function FinalizeConfirmLabel({ publish }: { publish: boolean }) {
  if (publish) {
    return (
      <T context="button, finalize an agent draft and publish it">
        Finalize and publish
      </T>
    );
  }
  return (
    <T context="button, finalize an agent draft without publishing it">
      Finalize without publishing
    </T>
  );
}

function StaleFinalizeDialog({
  warning,
  pending,
  onClose,
  onConfirm,
}: {
  warning: AgentDefinitionStaleBaseWarning | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog
      open={warning !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="dialog title, stale base warning for an agent draft">
              A newer version is published
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>{warning?.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            <T context="button, cancel stale agent draft finalization">
              Keep editing
            </T>
          </AlertDialogCancel>
          <Button disabled={pending} onClick={onConfirm}>
            <PendingIcon pending={pending} />
            <T context="button, finalize stale agent draft anyway">
              Finalize anyway
            </T>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PendingIcon({ pending }: { pending: boolean }) {
  if (!pending) return null;
  return <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />;
}

function useConfirmDiscardDraft() {
  const { discardDraft } = useAgentDefinitionForm();
  const { showToast } = useToast();
  const gt = useGT();
  return useCallback(
    async (versionId: string) => {
      try {
        await discardDraft(versionId);
        showToast({
          title: gt('Draft deleted', {
            context: 'toast after deleting an agent definition draft',
          }),
          message: gt(
            'The unpublished draft was removed. The published version is unchanged.',
            { context: 'toast after deleting an agent definition draft' },
          ),
          color: 'success',
        });
        return true;
      } catch (discardError) {
        showToast({
          title: gt('Could not delete draft', {
            context: 'toast after agent draft deletion fails',
          }),
          message: errorMessage(discardError),
          color: 'error',
        });
        return false;
      }
    },
    [discardDraft, gt, showToast],
  );
}

export function DiscardDraftConfirmDialog({
  open,
  version,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  version: number;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const gt = useGT();
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      closeOnConfirm={false}
      dangerous
      loading={pending}
      title={gt('Delete this draft?', {
        context: 'confirm dialog title, delete an agent draft',
      })}
      message={gt(
        'Delete unpublished draft v{version}? Everything in that draft is removed. The published version keeps running, and the agent is not archived.',
        {
          context: 'confirm dialog message, delete an agent draft',
          version: String(version),
        },
      )}
      confirmText={gt('Delete draft', {
        context: 'confirm button, delete an agent draft',
      })}
    />
  );
}

function EditingDraftBanner({
  notice,
}: {
  notice: Extract<DraftNotice, { kind: 'editing' }>;
}) {
  return (
    <div
      className={`mb-2 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${editingBannerClass(notice.parentIsPublished)}`}
    >
      <PencilSimpleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <EditingDraftCopy notice={notice} />
    </div>
  );
}

function editingBannerClass(parentIsPublished: boolean): string {
  if (parentIsPublished) {
    return 'border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-950/30 dark:text-sky-100';
  }
  return 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200';
}

function EditingDraftCopy({
  notice,
}: {
  notice: Extract<DraftNotice, { kind: 'editing' }>;
}) {
  if (notice.parentVersion === null) {
    return (
      <T context="banner while editing an agent draft with no recorded parent">
        Editing draft v<Var>{notice.draftVersion}</Var>. New runs keep using the
        published version until you finalize and publish this draft.
      </T>
    );
  }
  if (!notice.parentIsPublished) {
    return (
      <T context="banner while editing an agent draft copied from an older version">
        Editing draft v<Var>{notice.draftVersion}</Var>, copied from v
        <Var>{notice.parentVersion}</Var>, which is no longer the published
        version. Publishing this draft can replace newer changes. New runs keep
        using the published version until then.
      </T>
    );
  }
  return (
    <T context="banner while editing an agent draft copied from the published version">
      Editing draft v<Var>{notice.draftVersion}</Var>, copied from v
      <Var>{notice.parentVersion}</Var>. New runs keep using the published
      version until you finalize and publish this draft.
    </T>
  );
}

function ElsewhereDraftBanner({
  notice,
}: {
  notice: Extract<DraftNotice, { kind: 'elsewhere' }>;
}) {
  const { viewVersion, isDraftActionPending } = useAgentDefinitionForm();
  const [discardOpen, setDiscardOpen] = useState(false);
  const discard = useConfirmDiscardDraft();
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
      <WarningIcon className="h-4 w-4 flex-shrink-0" />
      <span className="min-w-0 flex-1">
        <ElsewhereDraftCopy notice={notice} />
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => void viewVersion(notice.draftId)}
      >
        <T context="button, open the personal agent draft">Resume draft</T>
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="text-destructive hover:text-destructive"
        onClick={() => setDiscardOpen(true)}
      >
        <T context="button, delete the personal agent draft that is not on screen">
          Discard draft
        </T>
      </Button>
      <DiscardDraftConfirmDialog
        open={discardOpen}
        version={notice.draftVersion}
        pending={isDraftActionPending}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => {
          void discard(notice.draftId).then((deleted) => {
            if (deleted) setDiscardOpen(false);
          });
        }}
      />
    </div>
  );
}

function ElsewhereDraftCopy({
  notice,
}: {
  notice: Extract<DraftNotice, { kind: 'elsewhere' }>;
}) {
  if (notice.parentVersion === null) {
    return (
      <T context="banner when a personal agent draft is not the version on screen">
        Unpublished draft v<Var>{notice.draftVersion}</Var> is not the version
        on this screen (v<Var>{notice.viewedVersion}</Var>). Edit will ask
        before opening it.
      </T>
    );
  }
  return (
    <T context="banner when a personal agent draft copied from another version is not on screen">
      Unpublished draft v<Var>{notice.draftVersion}</Var> (copied from v
      <Var>{notice.parentVersion}</Var>) is not the version on this screen (v
      <Var>{notice.viewedVersion}</Var>). Edit will ask before opening it.
    </T>
  );
}
